import { useEffect, useState } from "react";
import { BrandLockup } from "./BrandLockup";
import { readClientId } from "./personal";
import { doorApiBase, doorHome, refusalText, type SignInDoorName } from "./sign-in-doors";

type State =
  | { status: "reading" }
  | { status: "signing-in" }
  | { status: "done"; displayName: string }
  | { status: "failed"; message: string };

const ARRIVAL: Readonly<Record<SignInDoorName, { name: string; open: string }>> = {
  organizer: { name: "the staff workspace", open: "Open the staff workspace" },
  attendee: { name: "the convention guide", open: "Open the guide" },
  presenter: { name: "your presenter pages", open: "Open your presenter pages" },
  school: { name: "your school workspace", open: "Open your school workspace" },
};

/**
 * Where a phone lands after reading a square shown on another device.
 *
 * The code arrives after the `#` in the address, which browsers never send to a
 * server, so it stays out of request logs on the way in — this page reads it and
 * hands it over itself. It is cleared from the address bar immediately afterwards, so
 * a screenshot or a shared browser history holds nothing usable.
 *
 * One page for every door, reached at that door's own address.
 */
export function DeviceLinkLanding({ door }: { door: SignInDoorName }) {
  const [state, setState] = useState<State>({ status: "reading" });

  useEffect(() => {
    const token = new URLSearchParams(window.location.hash.replace(/^#/u, "")).get("t");
    if (!token) {
      setState({ status: "failed", message: "That link is missing its code. Show a new square on the other device." });
      return;
    }
    // Cleared before the request, not after, so a slow network still cannot leave the
    // code sitting in the address bar.
    window.history.replaceState(null, "", window.location.pathname);
    setState({ status: "signing-in" });
    void (async () => {
      try {
        const response = await fetch(`${doorApiBase(door)}/device-link/redeem`, {
          method: "POST",
          credentials: "same-origin",
          headers: { "content-type": "application/json" },
          // The guide names the device it is signing in, so its own record of which
          // devices hold a session matches what the rest of the app already calls this
          // browser.
          body: JSON.stringify({ token, clientId: readClientId() }),
        });
        if (!response.ok) throw new Error(await refusalText(response));
        const session = await response.json() as { displayName: string };
        setState({ status: "done", displayName: session.displayName });
      } catch (reason) {
        setState({
          status: "failed",
          message: reason instanceof Error ? reason.message : "This device could not be signed in.",
        });
      }
    })();
  }, [door]);

  const arrival = ARRIVAL[door];

  return (
    <div className="page attendee-account-page">
      <section className="attendee-account-card">
        <BrandLockup className="brand-lockup-card" />
        {state.status === "done" ? (
          <>
            <p className="status-kicker">This device is signed in</p>
            <h1 data-page-heading tabIndex={-1}>Welcome, {state.displayName}.</h1>
            <p>You will stay signed in on this device for a month, and longer if you keep using it.</p>
            <a className="primary-action" href={doorHome(door)}>{arrival.open}</a>
          </>
        ) : state.status === "failed" ? (
          <>
            <p className="status-kicker">Not signed in</p>
            <h1 data-page-heading tabIndex={-1}>That square did not work.</h1>
            <p>{state.message}</p>
            <p>A square lasts two minutes and works once, so the usual fix is to show a new one on the device you were already signed in on.</p>
            <a className="secondary-action" href={doorHome(door)}>Sign in with an email code instead</a>
          </>
        ) : (
          <>
            <p className="status-kicker">One moment</p>
            <h1 data-page-heading tabIndex={-1}>Signing this device into {arrival.name}…</h1>
          </>
        )}
      </section>
    </div>
  );
}
